import { Copy, Ellipsis, FolderGit2, Inbox, Pause, Play, Plus, RotateCw, Search, Square, Trash2 } from 'lucide-react';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { DataGate, PageBody } from '../components/shell/PageBody.tsx';
import { ComponentCatalogue } from '../features/design/ComponentCatalogue.tsx';
import { FoundationCatalogue } from '../features/design/FoundationCatalogue.tsx';
import { TokenCatalogue } from '../features/design/TokenCatalogue.tsx';
import {
  Avatar,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Dialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  ErrorState,
  Gauge,
  IconButton,
  Input,
  Kbd,
  Panel,
  RelativeTime,
  Segmented,
  Select,
  Skeleton,
  SkeletonText,
  Sparkline,
  StatusDot,
  StatusRing,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  Tooltip,
} from '@orgus/ui';
import { STATUS_TONES, TONE_LABEL } from '@orgus/ui';
import { useOrgus } from '../lib/store.ts';
import { toast } from '@orgus/ui';

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_minmax(0,1fr)] items-center gap-4 border-b border-border-subtle py-3 last:border-b-0">
      <span className="text-sm text-fg-subtle">{label}</span>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
  );
}

function CatalogueHeading({ id, title, sub }: { id: string; title: string; sub: string }) {
  return (
    <div id={id} className="mb-3 mt-8 flex flex-col gap-0.5 first:mt-0">
      <h2 className="text-lead text-fg">{title}</h2>
      <p className="text-ui text-fg-muted">{sub}</p>
    </div>
  );
}

/** The living catalogue: domain components, tokens, the shadcn foundation, the table, the charts and the primitives in every state. Reached from the command palette. */
export default function Design() {
  const [view, setView] = useState<'list' | 'board' | 'timeline'>('board');
  const [dialog, setDialog] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [name, setName] = useState('');
  const history = useOrgus((s) => Object.values(s.machineHistory)[0]);
  const now = Date.now();

  return (
    <PageBody>
      <DataGate>
        <CatalogueHeading id="components" title="Domain components" sub="DesignSystem.md 3.2 and 3.3: every component in every state." />
        <ComponentCatalogue />
        <CatalogueHeading id="tokens" title="Tokens" sub="Generated from packages/tokens/tokens/*.json. Switch theme or density to see them change." />
        <TokenCatalogue />
        <CatalogueHeading
          id="foundation"
          title="Foundation"
          sub="shadcn/ui on Radix, restyled to the design language (ADR 0033): selection controls, overlays, disclosure, the TanStack table, the visx charts and the one formatter."
        />
        <FoundationCatalogue />
        <CatalogueHeading id="primitives" title="Primitives" sub="Buttons, inputs, overlays and the rest of the library." />
        <div className="grid items-start gap-5 xl:grid-cols-2">
          <Panel title="Status">
            <Row label="Dots">
              {STATUS_TONES.map((t) => (
                <Tooltip key={t} content={TONE_LABEL[t]}>
                  <span tabIndex={0} className="inline-flex p-1">
                    <StatusDot tone={t} />
                  </span>
                </Tooltip>
              ))}
            </Row>
            <Row label="Badges">
              {STATUS_TONES.map((t) => (
                <Badge key={t} tone={t} dot>
                  {TONE_LABEL[t]}
                </Badge>
              ))}
            </Row>
            <Row label="Variants">
              <Badge tone="accent">Accent</Badge>
              <Badge tone="neutral" variant="outline">
                Outline
              </Badge>
              <Badge tone="failed" variant="solid">
                3
              </Badge>
              <Badge tone="neutral" icon={<FolderGit2 />}>
                Qawl-Agents
              </Badge>
            </Row>
          </Panel>

          <Panel title="Buttons" actions={<IconButton label="More" icon={<Ellipsis />} />}>
            <Row label="Variants">
              <Button variant="primary" icon={<Plus />} shortcut={['n']}>
                New session
              </Button>
              <Button icon={<Pause />}>Pause</Button>
              <Button variant="outline">Outline</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="danger" icon={<Square />}>
                Stop
              </Button>
            </Row>
            <Row label="Sizes and states">
              <Button size="sm">Small</Button>
              <Button size="lg">Large</Button>
              <Button loading>Saving</Button>
              <Button disabled>Disabled</Button>
            </Row>
            <Row label="Icon buttons">
              <IconButton label="Copy path" icon={<Copy />} />
              <IconButton label="Restart" icon={<RotateCw />} variant="secondary" />
              <IconButton label="Remove worktree" icon={<Trash2 />} variant="danger" />
              <IconButton label="Resume" icon={<Play />} loading />
            </Row>
          </Panel>

          <Panel title="Inputs">
            <div className="grid gap-4 sm:grid-cols-2">
              <Input label="Branch" placeholder="feat/new-thing" value={name} onChange={(e) => setName(e.target.value)} hint="A new worktree is created on this branch." />
              <Input label="Search" icon={<Search />} placeholder="Filter sessions" trailing={<Kbd keys={['/']} />} />
              <Select
                label="Machine"
                defaultValue="studio"
                options={[
                  { value: 'studio', label: 'studio' },
                  { value: 'mbp', label: 'mbp-16' },
                  { value: 'build', label: 'build-01 (offline)', disabled: true },
                ]}
              />
              <Input label="Path" defaultValue="C:/repos/missing" error="That folder does not exist on studio." />
              <Textarea wrapperClassName="sm:col-span-2" label="Prompt" placeholder="What should Claude do?" rows={3} />
            </div>
          </Panel>

          <Panel
            title="Switches and tabs"
            actions={
              <Segmented
                label="View"
                value={view}
                onChange={setView}
                options={[
                  { value: 'list', label: 'List' },
                  { value: 'board', label: 'Board' },
                  { value: 'timeline', label: 'Timeline' },
                ]}
              />
            }
          >
            <Tabs defaultValue="items">
              <TabsList>
                <TabsTrigger value="items" count={4}>
                  In flight
                </TabsTrigger>
                <TabsTrigger value="recent" count={8}>
                  Recent
                </TabsTrigger>
                <TabsTrigger value="logs">Logs</TabsTrigger>
              </TabsList>
              <TabsContent value="items" className="pt-3 text-base text-fg-muted">
                Selected view: {view}
              </TabsContent>
              <TabsContent value="recent" className="pt-3 text-base text-fg-muted">
                The last 20 finished items.
              </TabsContent>
              <TabsContent value="logs" className="pt-3 text-base text-fg-muted">
                Log output streams here.
              </TabsContent>
            </Tabs>
          </Panel>

          <Panel title="Data">
            <Row label="Sparkline">
              <Sparkline values={(history ?? []).map((s) => s.load)} min={0} max={1} width={160} height={32} label="CPU load" />
              <Sparkline values={(history ?? []).map((s) => s.mem)} min={0} max={1} width={160} height={32} tone="queued" label="Memory" />
            </Row>
            <Row label="Gauge">
              <div className="w-48">
                <Gauge value={0.42} label="CPU" />
              </div>
              <div className="w-48">
                <Gauge value={0.81} label="Memory" />
              </div>
              <Gauge value={0.75} variant="ring" label="Workers" valueText="3/4" />
            </Row>
            <Row label="Time">
              <RelativeTime ts={now - 4000} />
              <RelativeTime ts={now - 4 * 60_000} />
              <RelativeTime ts={now - 3 * 3_600_000} prefix="Updated" />
              <RelativeTime ts={null} />
            </Row>
            <Row label="Avatars">
              <Avatar name="Fix login race" />
              <Avatar name="Qawl-Agents" square size={28} />
              <StatusRing name="Approvals inbox" tone="working" />
              <StatusRing name="Webhook tests" tone="waiting" />
              <StatusRing name="Stripe retry" tone="idle" />
              <StatusRing name="Docs v2" tone="ended" />
            </Row>
            <Row label="Keys">
              <Kbd keys={['mod', 'k']} />
              <Kbd keys={['g', 's']} then />
              <Kbd keys={['shift', 'enter']} />
            </Row>
          </Panel>

          <Panel title="Overlays">
            <Row label="Dialogs">
              <Button onClick={() => setDialog(true)}>Open dialog</Button>
              <Button variant="danger" icon={<Trash2 />} onClick={() => setConfirm(true)}>
                Remove worktree
              </Button>
            </Row>
            <Row label="Menu">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button iconRight={<Ellipsis />}>Actions</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuLabel>Landing queue</DropdownMenuLabel>
                  <DropdownMenuItem icon={<Pause />}>Pause</DropdownMenuItem>
                  <DropdownMenuItem icon={<RotateCw />} shortcut={['shift', 'r']}>
                    Restart
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem icon={<Trash2 />} danger>
                    Remove project
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </Row>
            <Row label="Toasts">
              <Button onClick={() => toast.success('Session started', 'Claude is working in Qawl-Agents on studio.')}>Success</Button>
              <Button
                onClick={() =>
                  toast('Worktree removed', { description: 'C:/tmp/qawl-wt-old', action: { label: 'Undo', onClick: () => toast('Restored') } })
                }
              >
                With undo
              </Button>
              <Button
                onClick={() =>
                  toast.error({ what: 'Could not stop the session.', why: 'studio did not answer within 30 s.', next: 'Check Machines, then try again.' })
                }
              >
                Error
              </Button>
            </Row>
          </Panel>

          <Panel title="Loading and empty">
            <div className="grid gap-4 sm:grid-cols-2">
              <Card>
                <div className="mb-3 flex items-center gap-3">
                  <Skeleton round className="size-6" />
                  <Skeleton className="h-3 w-32" />
                </div>
                <SkeletonText lines={3} />
              </Card>
              <Card padded={false}>
                <EmptyState icon={<Inbox />} title="Nothing needs you" description="Waiting sessions and failed items show up here." />
              </Card>
            </div>
          </Panel>

          <Panel title="Errors">
            <ErrorState
              variant="inline"
              error={{ what: 'Could not read the landing queue.', why: 'The queue folder is missing on studio.', next: 'It appears the first time someone runs npm run land.' }}
              onRetry={() => new Promise((r) => setTimeout(r, 800))}
            />
            <ErrorState
              error={{ what: 'Could not reach the hub.', why: 'The connection failed before it answered.', next: 'Check that Orgus is running, then try again.' }}
              onRetry={() => new Promise((r) => setTimeout(r, 800))}
            />
          </Panel>
        </div>

        <Dialog
          open={dialog}
          onOpenChange={setDialog}
          title="New session"
          description="Claude starts in the checkout you pick."
          footer={
            <>
              <Button variant="ghost" onClick={() => setDialog(false)}>
                Cancel
              </Button>
              <Button variant="primary" shortcut={['mod', 'enter']} onClick={() => setDialog(false)}>
                Start session
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-4">
            <Select label="Project" options={[{ value: 'q', label: 'Qawl-Agents' }]} />
            <Textarea label="Prompt" placeholder="What should Claude do?" />
          </div>
        </Dialog>
        <ConfirmDialog
          open={confirm}
          onOpenChange={setConfirm}
          title="Remove worktree qawl-wt-voice-latency?"
          description="This deletes C:/tmp/qawl-wt-voice-latency on studio. Its branch fix/voice-latency stays. The folder has 6 changed files, so this will be refused."
          confirmLabel="Remove worktree"
          onConfirm={() =>
            new Promise((_, reject) =>
              setTimeout(
                () => reject({ what: 'studio refused to remove it.', why: 'It has 6 changed files.', next: 'Commit or discard them first.' }),
                900,
              ),
            )
          }
        />
      </DataGate>
    </PageBody>
  );
}
